import React, { useEffect, useRef, useState } from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import TocTocCTA from "@/components/TocTocCTA";
import SEOHead from "@/components/common/SEOHead";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Link } from "react-router-dom";

type CoverageCity = {
  name: string;
  region: string;
  description: string;
  services: string[];
  keywords: string;
  coordinates: [number, number];
};

const standardServices = [
  "Fotografia Imobiliária",
  "Vídeos Imobiliários",
  "Fotografia de Arquitetura",
  "Vídeo e Fotografia de Interiores",
  "Fotografia para MB&B",
  "Fotografia para Short Stay"
];

const coverageCities: CoverageCity[] = [
  {
    name: "Balneário Camboriú",
    region: "SC",
    description:
      "Fotografia imobiliária profissional em Balneário Camboriú. Atendemos edifícios de alto padrão, apartamentos, casas de luxo e imóveis comerciais. Especialistas em fotografia de arquitetura vertical.",
    services: standardServices,
    keywords:
      "fotografia imobiliária Balneário Camboriú, fotos apartamentos BC, vídeo imobiliário Balneário Camboriú",
    coordinates: [-26.9926, -48.635]
  },
  {
    name: "Camboriú",
    region: "SC",
    description:
      "Serviços de fotografia imobiliária em Camboriú. Fotografamos casas, apartamentos, terrenos e propriedades comerciais com qualidade profissional e entrega rápida em 48h.",
    services: standardServices,
    keywords: "fotografia imobiliária Camboriú, fotos casas Camboriú, vídeo imóvel Camboriú",
    coordinates: [-27.0245, -48.6503]
  },
  {
    name: "Itajaí",
    region: "SC",
    description:
      "Fotografia imobiliária profissional em Itajaí. Especializados em imóveis residenciais, comerciais e industriais. Fotos e vídeos que valorizam seu imóvel e aceleram a venda.",
    services: standardServices,
    keywords: "fotografia imobiliária Itajaí, fotos imóveis Itajaí, vídeo profissional Itajaí",
    coordinates: [-26.9101, -48.6705]
  },
  {
    name: "Itapema",
    region: "SC",
    description:
      "Fotografia imobiliária em Itapema com foco em imóveis de médio e alto padrão. Destaque para casas de frente para o mar, apartamentos e imóveis de temporada.",
    services: standardServices,
    keywords: "fotografia imobiliária Itapema, fotos apartamentos Itapema, vídeo imóvel praia Itapema",
    coordinates: [-27.085, -48.6169]
  },
  {
    name: "Porto Belo",
    region: "SC",
    description:
      "Serviços de fotografia imobiliária em Porto Belo. Fotografamos casas de praia, apartamentos, pousadas e propriedades para locação de temporada com qualidade profissional.",
    services: standardServices,
    keywords: "fotografia imobiliária Porto Belo, fotos casas praia Porto Belo, vídeo imóvel Porto Belo",
    coordinates: [-27.1523, -48.6056]
  },
  {
    name: "Bombinhas",
    region: "SC",
    description:
      "Fotografia imobiliária profissional em Bombinhas. Especializados em imóveis de temporada, casas de praia e apartamentos. Fotos que destacam a beleza natural da região.",
    services: standardServices,
    keywords: "fotografia imobiliária Bombinhas, fotos Airbnb Bombinhas, vídeo casa praia Bombinhas",
    coordinates: [-27.1386, -48.5146]
  },
  {
    name: "Navegantes",
    region: "SC",
    description:
      "Fotografia imobiliária em Navegantes. Atendemos residências, comércios e indústrias com fotografia profissional que valoriza seu patrimônio.",
    services: standardServices,
    keywords: "fotografia imobiliária Navegantes, fotos imóveis Navegantes, vídeo profissional Navegantes",
    coordinates: [-26.8947, -48.6546]
  }
];

const mapCenter: [number, number] = [-26.98, -48.62];
const mapZoom = 10;

const LEAFLET_SCRIPT_ID = "toctoc-leaflet-script";
const LEAFLET_STYLES_ID = "toctoc-leaflet-styles";
const LEAFLET_SCRIPT_SRC = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_STYLES_HREF = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";

type LeafletMapInstance = { remove: () => void };

let leafletLoader: Promise<any> | null = null;

const loadLeaflet = () => {
  if (leafletLoader) {
    return leafletLoader;
  }

  leafletLoader = new Promise<any>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Leaflet só pode ser carregado no navegador."));
      return;
    }

    const win = window as Window & { L?: any };
    if (win.L) {
      resolve(win.L);
      return;
    }

    const handleError = () => {
      leafletLoader = null;
      reject(new Error("Não foi possível carregar o Leaflet."));
    };

    const handleLoad = () => {
      const loaded = win.L;
      if (loaded) {
        resolve(loaded);
      } else {
        handleError();
      }
    };

    if (!document.getElementById(LEAFLET_STYLES_ID)) {
      const link = document.createElement("link");
      link.id = LEAFLET_STYLES_ID;
      link.rel = "stylesheet";
      link.href = LEAFLET_STYLES_HREF;
      link.crossOrigin = "";
      document.head.appendChild(link);
    }

    const existingScript = document.getElementById(LEAFLET_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = LEAFLET_SCRIPT_ID;
    script.src = LEAFLET_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = "";
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });

  return leafletLoader;
};

const CidadesAtendidas = () => {
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<LeafletMapInstance | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadMap = async () => {
      if (typeof window === "undefined" || mapInstanceRef.current || !mapContainerRef.current) {
        return;
      }

      try {
        const L = await loadLeaflet();

        if (!mapContainerRef.current || isCancelled) {
          return;
        }

        const map = L.map(mapContainerRef.current, {
          center: mapCenter,
          zoom: mapZoom,
          scrollWheelZoom: false,
          zoomControl: true
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contribuidores",
          maxZoom: 18
        }).addTo(map);

        const markerSvg = "<svg xmlns='http://www.w3.org/2000/svg' width='40' height='48' viewBox='0 0 27 40'><path fill='%23FF5605' d='M13.5 0C6.044 0 0 6.044 0 13.5c0 9.379 13.5 26.5 13.5 26.5S27 22.879 27 13.5C27 6.044 20.956 0 13.5 0z'/><circle cx='13.5' cy='13.5' r='5.5' fill='white'/></svg>";
        const markerIcon = L.icon({
          iconUrl: `data:image/svg+xml,${encodeURIComponent(markerSvg)}`,
          iconSize: [36, 48],
          iconAnchor: [18, 44],
          popupAnchor: [0, -40]
        });

        coverageCities.forEach((city) => {
          const marker = L.marker(city.coordinates, { icon: markerIcon }).addTo(map);
          const [firstSentence] = city.description.split(".");
          const popupText = `${(firstSentence ?? city.description).trim()}${firstSentence ? "." : ""}`;

          marker.bindPopup(
            `<div style="font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.4; max-width: 220px;">` +
              `<strong style="display:block; font-size: 15px; color: #323031; margin-bottom: 4px;">${city.name} - ${city.region}</strong>` +
              `<span style="color:#5B5A59;">${popupText}</span>` +
              "</div>"
          );

          marker.on("mouseover", function handleMouseOver() {
            this.openPopup();
          });
          marker.on("mouseout", function handleMouseOut() {
            this.closePopup();
          });
        });

        mapInstanceRef.current = map as LeafletMapInstance;
        setIsMapReady(true);
        setMapError(null);
      } catch (error) {
        if (!isCancelled) {
          console.error("Erro ao carregar o mapa de cobertura", error);
          setMapError("Não foi possível carregar o mapa interativo no momento.");
        }
      }
    };

    void loadMap();

    return () => {
      isCancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["WebPage", "CollectionPage"],
        "@id": "https://site.toctocbr.com/cidades-atendidas#webpage",
        "url": "https://site.toctocbr.com/cidades-atendidas",
        "name": "Cidades Atendidas — Toc Toc",
        "inLanguage": "pt-BR",
        "isPartOf": { "@id": "https://site.toctocbr.com/#website" },
        "about": [
          "fotógrafo imobiliário",
          "fotografia imobiliária profissional",
          "fotografia de arquitetura",
          "vídeo e fotografia de interiores",
          "fotografia para MB&B",
          "fotografia para short stay",
          "vídeos imobiliários"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://site.toctocbr.com/#website",
        "url": "https://site.toctocbr.com/",
        "name": "Toc Toc",
        "inLanguage": "pt-BR",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://site.toctocbr.com/?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
        "@id": "https://site.toctocbr.com/#org",
        "name": "Toc Toc",
        "url": "https://site.toctocbr.com/",
        "logo": "https://site.toctocbr.com/logo.png",
        "description": "Fotografia imobiliária profissional, fotografia de arquitetura/interiores, vídeos (filmmaker) e fotografia para MB&B, short stay e Airbnb em Santa Catarina.",
        "telephone": "+55-47-99736-7396",
        "email": "contato@toctocbr.com",
        "sameAs": ["https://www.instagram.com/toctoc.br"]
      },
      {
        "@type": "ItemList",
        "@id": "https://site.toctocbr.com/cidades-atendidas#lista-cidades",
        "name": "Cidades atendidas",
        "itemListOrder": "http://schema.org/ItemListOrderAscending",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "item": { "@type": "City", "name": "Balneário Camboriú" } },
          { "@type": "ListItem", "position": 2, "item": { "@type": "City", "name": "Camboriú" } },
          { "@type": "ListItem", "position": 3, "item": { "@type": "City", "name": "Itajaí" } },
          { "@type": "ListItem", "position": 4, "item": { "@type": "City", "name": "Itapema" } },
          { "@type": "ListItem", "position": 5, "item": { "@type": "City", "name": "Porto Belo" } },
          { "@type": "ListItem", "position": 6, "item": { "@type": "City", "name": "Bombinhas" } },
          { "@type": "ListItem", "position": 7, "item": { "@type": "City", "name": "Navegantes" } }
        ]
      },
      {
        "@type": "Service",
        "@id": "https://site.toctocbr.com/cidades-atendidas#servico-local",
        "serviceType": "Fotografia Imobiliária Profissional (SC)",
        "provider": { "@id": "https://site.toctocbr.com/#org" },
        "areaServed": [
          "Balneário Camboriú",
          "Camboriú",
          "Itajaí",
          "Itapema",
          "Porto Belo",
          "Bombinhas",
          "Navegantes",
          "Santa Catarina",
          "Brasil"
        ],
        "description": "Fotógrafo imobiliário, vídeos imobiliários, fotografia de arquitetura, vídeo e fotografia de interiores, fotografia para MB&B e short stay. Entrega em até 48 horas."
      },
      {
        "@type": "FAQPage",
        "@id": "https://site.toctocbr.com/cidades-atendidas#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Cobram taxa de cancelamento para as cidades atendidas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sim. Cancelamentos realizados com menos de 4 horas de antecedência geram taxa porque o fotógrafo já está a caminho e bloqueou a agenda para o horário. Até 4 horas de antecedência não há cobrança."
            }
          },
          {
            "@type": "Question",
            "name": "Qual o prazo de entrega das fotos?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nosso prazo padrão é de até 48 horas após a realização do ensaio fotográfico. As imagens são entregues editadas, em alta resolução e prontas para uso."
            }
          },
          {
            "@type": "Question",
            "name": "Atendem aos finais de semana?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sim. Atendemos também aos sábados e domingos mediante agendamento prévio, conforme disponibilidade."
            }
          },
          {
            "@type": "Question",
            "name": "Vocês oferecem pacotes para imobiliárias, corretores e hospedagens?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sim. Trabalhamos com pacotes mensais, trimestrais e personalizados para imobiliárias, corretores, hospedagens short stay e MB&B. Fale conosco para montar a melhor solução."
            }
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://site.toctocbr.com/cidades-atendidas#breadcrumbs",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://site.toctocbr.com/" },
          { "@type": "ListItem", "position": 2, "name": "Cidades Atendidas", "item": "https://site.toctocbr.com/cidades-atendidas" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Cidades Atendidas | Fotografia Imobiliária em Santa Catarina"
        description="BC, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas e Navegantes. Entrega em 48h e opção de atendimento fim de semana."
        keywords="cidades atendidas, fotógrafo imobiliário, fotografia imobiliária, vídeo e fotografia de interiores, fotografia de arquitetura, vídeos imobiliários, fotografia para MB&B, fotografia para short stay, Balneário Camboriú, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas, Navegantes, Santa Catarina"
        canonicalUrl="https://site.toctocbr.com/cidades-atendidas"
        schema={schema}
        titleTemplate={null}
        robots="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        ogTitle="Cidades Atendidas | Toc Toc Fotografia Imobiliária"
        ogDescription="SC: BC, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas e Navegantes. Entrega em 48h, fins de semana com agendamento e pacotes recorrentes."
        ogImage="https://site.toctocbr.com/og-image.jpg"
        ogImageAlt="Toc Toc — cobertura de cidades em Santa Catarina"
        twitterTitle="Cidades Atendidas | Toc Toc Fotografia Imobiliária"
        twitterDescription="Entrega em 48h, fins de semana com agendamento e pacotes recorrentes. Atendemos BC, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas e Navegantes."
        prefetchLinks={["/servicos", "/portfolio", "/sobre", "/contato"]}
      />
      
      <div className="min-h-screen bg-white">
        <TocTocHeader />
        
        <main>
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Fotografia Imobiliária em <span className="text-[#FF5605]">Santa Catarina</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed mb-8">
                  Atendemos as principais cidades do litoral catarinense com serviços profissionais de
                  fotografia imobiliária, vídeos imobiliários, fotografia de arquitetura, interiores e
                  hospedagens. Entrega em até 48 horas.
                </p>
                <div className="flex flex-wrap justify-center gap-6 text-lg">
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-[#FF5605]" />
                    <a href="tel:+5547997367396" className="hover:text-[#FF5605] transition-colors">
                      (47) 99736-7396
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#FF5605]" />
                    <a href="mailto:contato@toctocbr.com" className="hover:text-[#FF5605] transition-colors">
                      contato@toctocbr.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#FF5605]" />
                    <span>Seg-Sex: 8h-18h</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cities Grid */}
          <section className="py-16 bg-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                  Cidades <span className="text-[#FF5605]">Atendidas</span>
                </h2>
                <p className="text-lg text-[#5B5A59] max-w-3xl mx-auto">
                  Levamos fotografia imobiliária profissional para as principais cidades do litoral de Santa Catarina.
                  Os mesmos serviços premium em todas as regiões atendidas.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {coverageCities.map((city, index) => (
                  <article 
                    key={index}
                    className="bg-white rounded-2xl border-2 border-gray-100 p-8 hover:border-[#FF5605] hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-[#323031] mb-1">{city.name}</h3>
                        <div className="flex items-center text-[#5B5A59]">
                          <MapPin className="h-4 w-4 mr-1 text-[#FF5605]" />
                          <span className="text-sm">{city.region}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-[#5B5A59] mb-6 leading-relaxed">
                      {city.description}
                    </p>
                    
                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="text-sm font-semibold text-[#323031] mb-3">Serviços disponíveis:</h4>
                      <ul className="space-y-2">
                        {city.services.map((service, idx) => (
                          <li key={idx} className="flex items-center text-sm text-[#5B5A59]">
                            <span className="w-1.5 h-1.5 bg-[#FF5605] rounded-full mr-2"></span>
                            {service}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Coverage Map */}
          <section className="pb-8 md:pb-16 bg-white">
            <div className="container max-w-5xl mx-auto px-4">
              <div className="bg-gray-50 rounded-3xl overflow-hidden shadow-lg">
                <div className="p-8 md:p-10">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4 text-center">
                    Mapa de <span className="text-[#FF5605]">Cobertura</span>
                  </h2>
                  <p className="text-lg text-[#5B5A59] text-center max-w-2xl mx-auto">
                    Visualize os pontos onde atuamos no litoral de Santa Catarina: Balneário Camboriú,
                    Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas, Navegantes e regiões próximas.
                  </p>
                </div>
                <div className="relative">
                  <div className="relative h-[420px] w-full md:h-[500px]">
                    <div
                      ref={mapContainerRef}
                      className="h-full w-full"
                      role="presentation"
                      aria-hidden="true"
                    />
                    {!isMapReady && !mapError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80">
                        <div className="h-12 w-12 rounded-full border-4 border-[#FF5605]/20 border-t-[#FF5605] animate-spin mb-4" />
                        <p className="text-[#5B5A59] font-medium">Carregando mapa interativo...</p>
                      </div>
                    )}
                    {mapError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 px-6 text-center space-y-4">
                        <p className="text-[#5B5A59] font-medium">{mapError}</p>
                        <a
                          href="https://www.openstreetmap.org/#map=10/-26.98/-48.62"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF5605] font-semibold hover:underline"
                        >
                          Abrir mapa em nova janela
                        </a>
                      </div>
                    )}
                  </div>
                  <span className="sr-only">
                    Mapa destacando as cidades atendidas no litoral de Santa Catarina
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Coverage Area Info */}
          <section className="py-16 bg-gray-50">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="bg-gradient-to-r from-[#F6E5C9]/30 to-[#F6E5C9]/10 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-6">
                      Região Litorânea de <span className="text-[#FF5605]">Santa Catarina</span>
                    </h2>
                    <p className="text-lg text-[#5B5A59] mb-6 leading-relaxed">
                      Nossa base de operações fica em <strong>Balneário Camboriú</strong>, permitindo 
                      atendimento rápido e eficiente em toda a região litorânea de Santa Catarina.
                    </p>
                    <p className="text-lg text-[#5B5A59] mb-6 leading-relaxed">
                      Realizamos ensaios fotográficos de imóveis, vídeos imobiliários, fotografia de arquitetura
                      e interiores e fotografia para Airbnb e temporada.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#FF5605] flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                        <p className="text-[#5B5A59]">Entrega em até <strong>48 horas</strong> após a sessão</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#FF5605] flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                        <p className="text-[#5B5A59]">Edição profissional com <strong>correção de cores</strong> e tratamento de imagens</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#FF5605] flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                        <p className="text-[#5B5A59]">Pacotes recorrentes para <strong>corretores e imobiliárias</strong></p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-2xl shadow-xl p-8">
                    <h3 className="text-2xl font-bold text-[#323031] mb-6">Outras cidades?</h3>
                    <p className="text-[#5B5A59] mb-6">
                      Atendemos também outras cidades de Santa Catarina mediante consulta prévia.
                      Entre em contato para verificar disponibilidade, deslocamento e valores.
                    </p>
                    <div className="space-y-4">
                      <Link 
                        to="/servicos"
                        className="block w-full text-center bg-[#FF5605] hover:bg-[#E14D04] text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                      >
                        Ver Serviços
                      </Link>
                      <a 
                        href="https://wa.me/554797367396?text=Olá! Gostaria de saber mais sobre fotografia imobiliária na minha região."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center border-2 border-[#FF5605] text-[#FF5605] hover:bg-[#FF5605] hover:text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                      >
                        Solicitar Orçamento
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Local */}
          <section className="py-16 bg-white">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-[#323031] text-center mb-12">
                Perguntas <span className="text-[#FF5605]">Frequentes</span>
              </h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#323031] mb-3">
                    Vocês cobram taxa de cancelamento?
                  </h3>
                  <p className="text-[#5B5A59]">
                    Sim. Cancelamentos com menos de 4 horas de antecedência geram taxa, pois o fotógrafo já
                    está a caminho e reservou o horário. Com antecedência superior a 4 horas não há cobrança.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#323031] mb-3">
                    Qual o prazo de entrega das fotos?
                  </h3>
                  <p className="text-[#5B5A59]">
                    Nosso prazo padrão é de até 48 horas após a realização do ensaio fotográfico. 
                    As imagens são entregadas editadas, em alta resolução e prontas para uso.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#323031] mb-3">
                    Atendem aos finais de semana?
                  </h3>
                  <p className="text-[#5B5A59]">
                    Sim! Atendemos também aos sábados e domingos mediante agendamento prévio. 
                    Entre em contato para verificar disponibilidade.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-[#323031] mb-3">
                    Vocês oferecem pacotes para imobiliárias, corretores e hospedagens?
                  </h3>
                  <p className="text-[#5B5A59]">
                    Sim! Trabalhamos com pacotes mensais, trimestrais e personalizados para imobiliárias,
                    corretores, hospedagens short stay e MB&B. Podemos montar a solução ideal conforme a
                    demanda de cada cliente.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <TocTocCTA />
        </main>
        
        <TocTocFooter />
      </div>
    </>
  );
};

export default CidadesAtendidas;
